import type { ArtifactKind } from '@inference.engine/widgets';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  Spinner,
  Textarea,
} from '@monksflow/monks-ui';
import { FolderInputIcon, Link2Icon, PlusIcon, UploadIcon, XIcon } from 'lucide-react';
import { ExistingArtifactsPicker, type WorkspaceArtifactRow } from '@/entities/artifact';
import { formatFileSize } from '@/shared/utils/format';
import { useTranslation } from 'react-i18next';
import { BriefTemplateLevelChoice } from './BriefTemplateLevelChoice';
import { BriefTemplateStartModeChoice } from './BriefTemplateStartModeChoice';
import type { BriefTemplateIntakeShell } from './useBriefTemplateIntakeShell';

interface AttachmentRowProps {
  primary: string;
  secondary?: string;
  removeLabel: string;
  onRemove: () => void;
}

const AttachmentRow = ({ primary, secondary, removeLabel, onRemove }: AttachmentRowProps) => (
  <li className="flex items-center gap-2 rounded-lg border border-border bg-background p-2">
    <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
      <Link2Icon className="size-4 text-foreground" aria-hidden />
    </div>
    <div className="flex min-w-0 flex-1 items-center gap-2 text-sm font-medium text-foreground">
      <span className="truncate">{primary}</span>
      {secondary && (
        <span className="shrink-0 text-xs font-normal text-muted-foreground">{secondary}</span>
      )}
    </div>
    <Button variant="ghost" size="mini" aria-label={removeLabel} onClick={onRemove}>
      <XIcon className="size-4" />
    </Button>
  </li>
);

export const GeneratingView = () => {
  const { t } = useTranslation('briefs');
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 py-12 text-center">
      <Spinner />
      <h3 className="text-base font-semibold text-foreground">
        {t('briefTemplateCreation.briefTemplateIntakeModal.generatingBriefTemplate')}
      </h3>
      <p className="text-sm text-muted-foreground">
        {t('briefTemplateCreation.briefTemplateIntakeModal.waitAMomentUntilWe')}
      </p>
    </div>
  );
};

/**
 * Body content for the three views the intake flow swaps between (generating, form, picker).
 * Pure presentational — every value and handler comes from {@link useBriefTemplateIntakeShell}.
 * Shared verbatim by the Dialog and Sheet shells.
 */
export function BriefTemplateIntakeBody({ shell }: { shell: BriefTemplateIntakeShell }) {
  const { t } = useTranslation('briefs');
  const { form } = shell;

  // A blank template saves in one request, so the form stays in view under the locked footer.
  if (shell.isGenerating && !shell.isBlank) return <GeneratingView />;

  if (shell.isFormStep) {
    return (
      <div className="flex flex-col gap-4">
        <BriefTemplateStartModeChoice
          value={shell.startMode}
          onChange={shell.setStartMode}
          disabled={shell.isGenerating}
        />

        <div className="flex flex-col gap-1.5">
          <Label htmlFor={shell.titleFieldId}>
            {t('briefTemplateCreation.briefTemplateIntakeModal.title')}{' '}
            <span className="text-destructive">*</span>
          </Label>
          <Input
            id={shell.titleFieldId}
            placeholder={t('briefTemplateCreation.briefTemplateIntakeModal.enterTemplateTitle')}
            value={form.values.title}
            onChange={(event) => form.setTitle(event.target.value)}
            disabled={shell.isGenerating}
            className="bg-input"
          />
        </div>

        {shell.canChooseLevel && (
          <BriefTemplateLevelChoice
            value={shell.level}
            onChange={shell.setLevel}
            disabled={shell.levelChoiceLocked || shell.isGenerating}
          />
        )}

        <div className="border-t border-border" />

        {shell.isBlank ? (
          <p className="text-sm text-muted-foreground">
            {t('briefTemplateCreation.briefTemplateIntakeModal.startBlankHint')}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold">
              {t('briefTemplateCreation.briefTemplateIntakeModal.addContext')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t('briefTemplateCreation.briefTemplateIntakeModal.uploadContextToEnrichYour')}
            </p>

            {/* Instructions, files, and artifacts render here but are NOT
              serialised on submit — see getSubmitPayload JSDoc. */}
            <Textarea
              id={shell.instructionsFieldId}
              placeholder={t(
                'briefTemplateCreation.briefTemplateIntakeModal.describeWhatYouWantTo',
              )}
              value={form.values.instructions}
              onChange={(event) => form.setInstructions(event.target.value)}
              rows={3}
              className="bg-input"
            />

            <div className="rounded-md bg-input p-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full bg-background">
                    {t('briefTemplateCreation.briefTemplateIntakeModal.addContext')}
                    <PlusIcon className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-(--radix-dropdown-menu-trigger-width)"
                >
                  <DropdownMenuItem onSelect={shell.triggerUpload}>
                    <UploadIcon className="size-4" />
                    {t('briefTemplateCreation.briefTemplateIntakeModal.uploadFile')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => shell.setStep('picker')}>
                    <FolderInputIcon className="size-4" />
                    {t('briefTemplateCreation.briefTemplateIntakeModal.browseMonksFlowArtifacts')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {shell.hasAttachments && (
              <ul className="flex flex-col gap-2">
                {form.values.files.map((file, index) => (
                  <AttachmentRow
                    key={`file-${file.name}-${index}`}
                    primary={file.name}
                    secondary={formatFileSize(file.size)}
                    removeLabel={t('briefTemplateCreation.briefTemplateIntakeModal.removeFile', {
                      fileName: file.name,
                    })}
                    onRemove={() => form.removeFile(index)}
                  />
                ))}
                {form.values.artifacts.map((artifact) => (
                  <AttachmentRow
                    key={`artifact-${artifact.id}`}
                    primary={artifact.name}
                    removeLabel={t(
                      'briefTemplateCreation.briefTemplateIntakeModal.removeArtifact',
                      {
                        name: artifact.name,
                      },
                    )}
                    onRemove={() => form.removeArtifact(artifact.id)}
                  />
                ))}
              </ul>
            )}

            <input
              id={shell.fileInputId}
              type="file"
              multiple
              aria-label={t('briefTemplateCreation.briefTemplateIntakeModal.attachFiles')}
              className="sr-only"
              onChange={shell.handleFileInput}
            />
          </div>
        )}
      </div>
    );
  }

  if (shell.workspaceId) {
    return (
      <ExistingArtifactsPicker
        workspaceId={shell.workspaceId}
        attachedIds={shell.emptyAttachedIds}
        selectedIds={shell.pickerSelectedIds}
        kindFilter={shell.pickerKindFilter}
        searchInput={shell.pickerSearch}
        sort={shell.pickerSort}
        isUserInput={false}
        onSelectedIdsChange={shell.setPickerSelectedIds}
        onKindFilterChange={(kind: ArtifactKind | null) => shell.setPickerKindFilter(kind)}
        onSearchInputChange={shell.setPickerSearch}
        onSortChange={shell.setPickerSort}
      />
    );
  }

  return (
    <p className="text-sm text-muted-foreground">
      {t('briefTemplateCreation.briefTemplateIntakeModal.selectAWorkspaceToContinue')}
    </p>
  );
}

// Re-exported so both shells can key the picker rows the same way without importing the entity twice.
export type { WorkspaceArtifactRow };
